import { useLocalStorage } from '@shared/hooks/localStorage';
import { createContext, useContext, useLayoutEffect, useMemo } from 'react';

export enum Theme {
  light = 'light',
  dark = 'dark',
}

type IThemeContext = {
  /**
   * Get the current theme.
   * @returns Theme
   * @example
   * const { theme } = useTheme();
   * console.log(theme); // Theme.light
   */
  theme: Theme;
  /**
   * Set the theme to the new theme.
   * If the new theme is a function, it will be called with the previous theme.
   * If the new theme is not provided, it will toggle between light and dark themes.
   * @param newTheme The new theme or a function that returns the new theme.
   * @returns void
   * @example
   * setTheme(Theme.light);
   * setTheme(Theme.dark);
   * setTheme(); // Toggles between light and dark themes
   * setTheme((prevTheme) => (prevTheme === Theme.light ? Theme.dark : Theme.light));
   */
  setTheme: (newTheme?: Theme | ((prevTheme: Theme) => Theme)) => void;
};

// Exported so Storybook can seed a theme before ThemeProvider's first read.
export const THEME_STORAGE_KEY = 'color-scheme';
const MANTINE_COLOR_SCHEME_ATTRIBUTE = 'data-mantine-color-scheme';

const ThemeContext = createContext<IThemeContext>({} as IThemeContext);

export const ThemeProvider: FCC = ({ children }) => {
  const [theme, setTheme] = useLocalStorage<Theme>(THEME_STORAGE_KEY, Theme.dark);

  useLayoutEffect(() => {
    document.documentElement.setAttribute(MANTINE_COLOR_SCHEME_ATTRIBUTE, theme);
    document.body.classList[theme === Theme.light ? 'remove' : 'add'](Theme.dark);

    const observer = new MutationObserver(() => {
      if (document.documentElement.getAttribute(MANTINE_COLOR_SCHEME_ATTRIBUTE) !== theme) {
        document.documentElement.setAttribute(MANTINE_COLOR_SCHEME_ATTRIBUTE, theme);
      }
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, [theme]);

  const themeContext = useMemo<IThemeContext>(
    () => ({
      theme,
      setTheme: (newTheme) =>
        setTheme((prevTheme) =>
          typeof newTheme === 'function'
            ? newTheme(prevTheme)
            : (newTheme ?? (prevTheme === Theme.light ? Theme.dark : Theme.light)),
        ),
    }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={themeContext}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
